import { createContext, useCallback, useContext, useState } from "react";

import { Alert, Snackbar } from "@mui/material";

type Severity = "success" | "info" | "warning" | "error";

type Toast = { id: number; severity: Severity; message: string };

type Ctx = {
  toast: (message: string, severity?: Severity) => void;
};

const ToastCtx = createContext<Ctx | null>(null);

let counter = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [queue, setQueue] = useState<Toast[]>([]);

  const toast = useCallback((message: string, severity: Severity = "info") => {
    counter += 1;
    setQueue((q) => [...q, { id: counter, severity, message }]);
  }, []);

  const handleClose = (id: number) => {
    setQueue((q) => q.filter((t) => t.id !== id));
  };

  return (
    <ToastCtx.Provider value={{ toast }}>
      {children}
      {queue.map((t, idx) => (
        <Snackbar
          key={t.id}
          open
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          autoHideDuration={t.severity === "error" ? 8000 : 4000}
          onClose={(_, reason) => {
            if (reason === "clickaway") return;
            handleClose(t.id);
          }}
          sx={{ mb: idx * 7 }}
        >
          <Alert
            severity={t.severity}
            variant="filled"
            onClose={() => handleClose(t.id)}
            sx={{ width: "100%", maxWidth: 480 }}
          >
            {t.message}
          </Alert>
        </Snackbar>
      ))}
    </ToastCtx.Provider>
  );
}

export function useToast(): (message: string, severity?: Severity) => void {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx.toast;
}
